import { XMarkIcon } from "@heroicons/react/24/outline";
import Dialog from ".";

export const BOT_STRATEGIES = {
  REBALANCE: "rebalance",
  GRID: "grid",
  XENO_ARC: "xeno arc",
  XENO_V3: "xeno bot v3",
  DCA: "dca",
};
// Helper to render text with **bold** markdown style
const renderTextWithBold = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} className="font-semibold text-gray-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const BOT_CONTENT = {
  [BOT_STRATEGIES.XENO_ARC]: {
    title: "Xenoarc",
    sections: [
      {
        heading: "What is Xenoarc?",
        text: `Xenoarc is a step-based range-trading bot that automates a simple idea: buy on dips, take profit on bounces. It works around the current market price and breaks price movement into steps so it can work volatility for you, day and night.`,
      },
      {
        heading: "How It Trades",
        list: [
          "Starts from the current market price and builds a series of trading steps around it.",
          "Buys gradually as price moves into more attractive zones, instead of going all-in at once.",
          "Takes profit in stages as price moves back in your favour.",
          "Adjusts its working zone over time so it can keep trading even when the market trends away from the starting point.",
          "Uses internal confirmation logic to avoid reacting to every tiny price wiggle, focusing instead on more meaningful moves.",
          "Can scale its position size after strong moves so that big swings have a bigger impact on your overall results.",
        ],
      },
      {
        heading: "Why It’s Different",
        list: [
          "No fixed top/bottom range to guess – you don’t need to predict exact extremes.",
          "Capital-aware engine – your capital and trade size define how many steps the bot can support.",
          "Works with volatility instead of fighting it – designed for markets that move in waves.",
          "Built for both beginners and advanced users – start with presets, then customise when you’re ready.",
        ],
      },
      {
        heading: "Key Benefits for Users",
        list: [
          "No fixed top/bottom range to guess – you don’t need to predict exact extremes.",
          "Capital-aware engine – your capital and trade size define how many steps the bot can support.",
          "Works with volatility instead of fighting it – designed for markets that move in waves.",
          "Built for both beginners and advanced users – start with presets, then customise when you’re ready.",
        ],
      },
      {
        heading: "How to Set It Up",
        text: "Setting up Xenoarc on the platform is designed to be simple. You’ll see a few key fields:",
        list: [
          "**Exchange** – choose the connected exchange where the bot will trade.",
          "**Pair** – pick the coin pair you want Xenoarc to run on.",
          "**Capital allocation** – set how much of your balance you want this bot to use.",
          "**Trade size per step** – decide how much is used for each trading action.",
          "**Profile & presets** – choose a ready-made setup like Conservative, Balanced, or Aggressive based on your risk comfort.",
          "**Customized settings (optional)** – for users who want more control over how Xenoarc behaves:",
          "**Number of steps (levels)** – sets how many price steps the bot uses to spread entries and exits.",
          "**Buy-step width** – controls how closely spaced your dip-buying opportunities are.",
          "**Sell-step width** – controls how often the bot looks to realise profit as price moves up.",
          "**Buy confirmation (buy-flag return)** – adds an extra confirmation check before the bot commits to a buy.",
          "**Sell confirmation (exit-flag return)** – adds an extra confirmation check before the bot locks in profit.",
        ],
        footer:
          "Once you're happy with the setup, start the bot and monitor its performance from your dashboard.",
      },
    ],
  },

  [BOT_STRATEGIES.GRID]: {
    title: "Grid",
    sections: [
      {
        heading: "What is Grid Bot?",
        text: `The Xenorize Grid Bot is a range-trading bot that aims to profit from price swings. It places multiple buy and sell levels so it can repeatedly buy lower and sell higher when the market moves up and down.`,
      },
      {
        heading: "How It Trades",
        list: [
          "Builds a ladder of buy levels below the market and sell levels above it.",
          "Buys when price dips into a lower level.",
          "Sells when price bounces into a higher level.",
          "Keeps the grid running so long as your configuration and capital allow.",
        ],
      },
      {
        heading: "Why It's Different",
        list: [
          "Independent buy and sell spacing – you can tune how often it buys vs how often it takes profit.",
          "Simple, visible logic – no hidden conditions, just a clear grid of levels.",
          "Multiple profiles – start with Conservative, Balanced, or Aggressive, then tweak as you gain experience.",
        ],
      },
      {
        heading: "Key Benefits for Users",
        list: [
          "Turns sideways and choppy markets into a trading opportunity.",
          "Removes the need to watch the chart and manually place orders.",
          'Gives you a transparent structure for "buy low, sell high" behaviour.',
        ],
      },
      {
        heading: "How to Set It Up",
        text: "Setting up the Grid Bot is straightforward. You'll see a few key fields:",
        list: [
          "**Exchange** – where the bot will trade.",
          "**Pair** – which coin pair the grid runs on.",
          "**Capital allocation** – how much of your balance the bot can use.",
          "**Trade size** – how much is used per level/order.",
          "**Profile & presets** – pick a ready-made setup like Conservative, Balanced, or Aggressive.",
          "Customized settings – for users who want more control over the grid:",
          "**Number of step (levels)** – how many price levels the grid uses to spread out buys and sells.",
          "**Buy-step width (%)** – how closely each buy level is spaced as price moves down.",
          "**Sell-step width (%)** – how closely each sell level is spaced as price moves up.",
        ],
        footer:
          "Once you're satisfied with the configuration, start the bot and track its performance from your dashboard.",
      },
    ],
  },

  [BOT_STRATEGIES.DCA]: {
    title: "DCA",
    sections: [
      {
        heading: "What is DCA Bot?",
        text: `The Xenorize DCA Bot helps you build a position over time instead of buying all at once. It automatically spreads your entries into multiple buys as the market moves, helping smooth out your average entry price.`,
      },
      {
        heading: "How It Trades",
        list: [
          "Follows a coin's price and buys in chunks when your conditions are met.",
          "Adjusts your average buy price as new orders are filled.",
          "Includes a built-in take-profit feature to help you lock in gains when the market jumps.",
        ],
      },
      {
        heading: "Why It's Different",
        list: [
          "Price-driven, not purely time-driven – reacts to meaningful price moves instead of just buying on a fixed schedule.",
          "Take-profit aware – doesn't just accumulate forever; it also has rules to secure profit.",
          "Capital-controlled – you choose Trade Size and the maximum number of orders the bot can place.",
        ],
      },
      {
        heading: "Key Benefits for Users",
        list: [
          'Reduces the stress of picking a single "perfect" entry price.',
          "Automatically buys more when conditions match your plan.",
          "Helps lock in profit on sudden pumps, even if you're not watching the chart.",
        ],
      },
      {
        heading: "How to Set It Up",
        text: "Setting up the DCA Bot is straightforward. You'll see a few key fields:",
        list: [
          "**Exchange** – pick the connected exchange where the bot trades.",
          "**Pair** – choose the coin pair you want to accumulate.",
          "**Capital allocation** – decide how much of your balance this bot can use.",
          "**Trade size** – set how much is used for each buy.",
          "**Profile & presets** – start with a ready-made setup like Conservative, Balanced, or Aggressive.",
          "Customized settings – for users who want more control over the DCA:",
          "**Maximum order** – cap on how many DCA buys the bot can place in this run.",
          "**Price change (%)** – how much the price needs to move before the next buy is triggered.",
          "**Take profit (%)** – the gain level where the bot is allowed to secure profit.",
          "**Trigger price** – the price level from which your DCA plan starts working.",
        ],
        footer:
          "Once you're happy with the setup, start the bot and monitor its performance from your dashboard.",
      },
    ],
  },

  [BOT_STRATEGIES.REBALANCE]: {
    title: "Rebalance Bot",
    sections: [
      {
        heading: "What is Rebalance?",
        text: `Rebalance bot maintains asset ratios inside your portfolio.`,
      },
      {
        heading: "How It Works",
        list: [
          "Automatically re-adjusts distribution (e.g., 50% BTC / 50% ETH).",
          "Buys the weaker asset & sells stronger one.",
          "Reduces risk and smoothens long-term performance.",
        ],
      },
    ],
  },
};

const DialogBotInfo = ({ open, onClose, botStrategy }) => {
  const content = BOT_CONTENT[botStrategy];

  if (!content) return null;

  // Function to handle smooth scrolling
  const scrollToSection = (index) => {
    const element = document.getElementById(`bot-info-section-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="900px"
      className="!bg-white !ring-1 !ring-gray-300 !p-0 overflow-hidden rounded-xl"
    >
      <div>
        {/* Header */}
        <div className="bg-[#302571] text-white relative h-[100px] flex justify-center items-center  rounded-xl">
          <button
            onClick={onClose}
            className="bg-transparent text-white absolute top-1 right-1 hover:bg-white/10 rounded-full p-0 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          <h2 className="text-3xl font-bold text-center tracking-wide">
            {content.title}
          </h2>
        </div>

        {/* Content Body */}
        <div className="max-h-[70vh] overflow-y-auto mt-6 custom-scrollbar no-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Left Column: Detailed Content */}
            <div className="md:col-span-2 space-y-8">
              {content.sections.map((sec, idx) => (
                <div
                  key={idx}
                  id={`bot-info-section-${idx}`} // Unique ID for scrolling
                  className="space-y-3 scroll-mt-4" // scroll-mt adds a little padding at top when scrolled to
                >
                  <h3 className="text-xl font-bold text-gray-900">
                    {sec.heading}
                  </h3>

                  {sec.text && (
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                      {sec.text}
                    </p>
                  )}

                  {sec.list && (
                    <ul className="text-gray-600 text-sm list-disc ml-4 space-y-2">
                      {sec.list.map((item, i) => (
                        <li key={i} className="pl-1">
                          {renderTextWithBold(item)}
                        </li>
                      ))}
                    </ul>
                  )}

                  {sec.footer && (
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line mt-2">
                      {sec.footer}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Right Column: Table of Contents */}
            <div className="md:col-span-1 border-l border-gray-200 pl-6 hidden md:block">
              <div className="sticky top-0 pt-2">
                <ul className="space-y-4">
                  {content.sections.map((sec, idx) => (
                    <li
                      key={idx}
                      onClick={() => scrollToSection(idx)}
                      className="text-sm font-medium text-gray-800 hover:text-[#302571] cursor-pointer transition-colors"
                    >
                      {sec.heading}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default DialogBotInfo;
